import { EventTypeData } from '../types/ViewModel/EventTypeData';
import { EventTypeDataVersion } from '../types/ViewModel/EventTypeDataVersion';
import { ConditionalMandatoryFactory } from './ConditionalMandatoryFactory';
import { EventTypeDataVersionFactory } from './EventTypeDataVersionFactory';
import { EventTypeDataBM } from '../types/BusinessModel/EventTypeDataBM';
import EventTypeLevelFactory from './EventTypeLevelFactory';
import { CrossPortCallsConditionFactory } from './CrossPortCallsConditionFactory';
import { EventTypeLevel } from '../types/ViewModel/EventTypeLevel';
import { CrossPortCallsCondition } from '../types/ViewModel/CrossPortCallsCondition';
import { ImosPortFunctionMapFactory } from './ImosPortFunctionMapFactory';
import { ImosPortFunctionMap } from '../types/ViewModel/ImosPortFunctionMap';
import { EventTypeFilterFactory } from './EventTypeFilterFactory';
import AlternateEventTypeGroupMapFactory from './AlternateEventTypeGroupMapFactory';
import { EventTypeInputDelayEventTypeMapFactory } from './EventTypeInputDelayEventTypeMapFactory';
import { EventTypeInputDelayEventTypeMap } from '../types/ViewModel/EventTypeInputDelayEventTypeMap';
import { EventTypeInputExcludeTimeEventTypeMapFactory } from './EventTypeInputExcludeTimeEventTypeMapFactory';
import { EventTypeInputExcludeTimeEventTypeMap } from '../types/ViewModel/EventTypeInputExcludeTimeEventTypeMap';
import { EventTypeGroupConfigChangeFactory } from './EventTypeGroupConfigChangeFactory';
import { EventTypeGroupConfigChange } from '../types/ViewModel/EventTypeGroupConfigChange';
import { useVesselStore } from '../context/VesselContext';

export default () => {
    const conditionalMandatoryFactory = new ConditionalMandatoryFactory();
    const eventTypeDataVersionFactory = new EventTypeDataVersionFactory();
    const crossPortCallsConditionFactory = new CrossPortCallsConditionFactory();
    const imosPortFunctionMapFactory = new ImosPortFunctionMapFactory();
    const eventTypeFilterFactory = new EventTypeFilterFactory();
    const eventTypeInputDelayEventTypeMapFactory = new EventTypeInputDelayEventTypeMapFactory();
    const eventTypeInputExcludeTimeEventTypeMapFactory = new EventTypeInputExcludeTimeEventTypeMapFactory();
    const eventTypeGroupConfigChangeFactory = new EventTypeGroupConfigChangeFactory();
    const { newAlternativeEventtypeGroupMapArray } = AlternateEventTypeGroupMapFactory();
    const { newEventTypeLevelArray } = EventTypeLevelFactory();
    const { selectedVessel } = useVesselStore();

    const newEventTypeData = (eventTypeDataBM: EventTypeDataBM): EventTypeData => {
        const evLevelArray = newEventTypeLevelArray(eventTypeDataBM.eventTypeLevels);
        const condMandatoryArray = conditionalMandatoryFactory.newConditionalMandatoryArray(eventTypeDataBM.conditionalMandatoryList);
        const crossPcCondArray = crossPortCallsConditionFactory.newCrossPortCallsConditionArray(eventTypeDataBM.crossPortCallsConditionList);
        const imosPortFuncMapArray = imosPortFunctionMapFactory.newImosPortFunctionMapArray(eventTypeDataBM.imosPortFunctionMapList);
        let eventTypeInputDelayEventTypeMapArray: EventTypeInputDelayEventTypeMap[] = [];
        if (eventTypeDataBM.eventTypeInputDelay) {
            eventTypeInputDelayEventTypeMapArray = eventTypeInputDelayEventTypeMapFactory.newEventTypeInputDelayEventTypeMapList(
                eventTypeDataBM.eventTypeInputDelay.eventTypeInputDelayEventTypeMapList
            );
        }
        let eventTypeInputExcludeTimeEventTypeMapArray: EventTypeInputExcludeTimeEventTypeMap[] = [];
        if (eventTypeDataBM.eventTypeInputDelay) {
            eventTypeInputExcludeTimeEventTypeMapArray = eventTypeInputExcludeTimeEventTypeMapFactory.newEventTypeInputExcludeTimeEventTypeMapList(
                eventTypeDataBM.eventTypeInputDelay.eventTypeInputExcludeTimeEventTypeMapList
            );
        }
        let eventTypeGroupConfigChangeArray: EventTypeGroupConfigChange[] = [];
        if (eventTypeDataBM.eventTypeGroupConfigChangeList) {
            eventTypeGroupConfigChangeArray = eventTypeGroupConfigChangeFactory.newEventTypeGroupConfigChangeList(
                eventTypeDataBM.eventTypeGroupConfigChangeList
            );
        }
        const eventTypeFilterList = eventTypeFilterFactory.newEventTypeFilterList(eventTypeDataBM.eventTypeFilterList);
        let eventTypeDataVersion: EventTypeDataVersion | null = null;
        if (eventTypeDataBM.version) {
            eventTypeDataVersion = eventTypeDataVersionFactory.newEventTypeDataVersion(eventTypeDataBM.version);
        }
        updateCrossPortCallCondition(evLevelArray, crossPcCondArray);
        updateImosPortFunctionMap(evLevelArray, imosPortFuncMapArray);
        updateEventTypeInputDelayEventTypeMap(evLevelArray, eventTypeInputDelayEventTypeMapArray);
        updateEventTypeInputExcludeTimeEventTypeMap(evLevelArray, eventTypeInputExcludeTimeEventTypeMapArray);
        updateEventTypeGroupConfigChange(evLevelArray, eventTypeGroupConfigChangeArray);
        return new EventTypeData(
            evLevelArray,
            condMandatoryArray,
            eventTypeDataVersion,
            eventTypeFilterList,
            newAlternativeEventtypeGroupMapArray(eventTypeDataBM.alternateEventTypeGroupMapList),
            selectedVessel?.hasOrcaWebSimpleMode
        );
    };

    const newEventTypeDataContext = (eventTypeDataBM: EventTypeDataBM, ctx: number): EventTypeData => {
        const evLevelArray = newEventTypeLevelArray(eventTypeDataBM.eventTypeLevels.filter((level) => level.eventTypeContextId === ctx));
        const condMandatoryArray = conditionalMandatoryFactory.newConditionalMandatoryArray(eventTypeDataBM.conditionalMandatoryList);
        const crossPcCondArray = crossPortCallsConditionFactory.newCrossPortCallsConditionArray(eventTypeDataBM.crossPortCallsConditionList);
        const imosPortFuncMapArray = imosPortFunctionMapFactory.newImosPortFunctionMapArray(eventTypeDataBM.imosPortFunctionMapList);
        let eventTypeInputDelayEventTypeMapArray: EventTypeInputDelayEventTypeMap[] = [];
        if (eventTypeDataBM.eventTypeInputDelay) {
            eventTypeInputDelayEventTypeMapArray = eventTypeInputDelayEventTypeMapFactory.newEventTypeInputDelayEventTypeMapList(
                eventTypeDataBM.eventTypeInputDelay.eventTypeInputDelayEventTypeMapList
            );
        }
        let eventTypeInputExcludeTimeEventTypeMapArray: EventTypeInputExcludeTimeEventTypeMap[] = [];
        if (eventTypeDataBM.eventTypeInputDelay) {
            eventTypeInputExcludeTimeEventTypeMapArray = eventTypeInputExcludeTimeEventTypeMapFactory.newEventTypeInputExcludeTimeEventTypeMapList(
                eventTypeDataBM.eventTypeInputDelay.eventTypeInputExcludeTimeEventTypeMapList
            );
        }
        let eventTypeGroupConfigChangeArray: EventTypeGroupConfigChange[] = [];
        if (eventTypeDataBM.eventTypeGroupConfigChangeList) {
            eventTypeGroupConfigChangeArray = eventTypeGroupConfigChangeFactory.newEventTypeGroupConfigChangeList(
                eventTypeDataBM.eventTypeGroupConfigChangeList
            );
        }
        const eventTypeFilterList = eventTypeFilterFactory.newEventTypeFilterList(eventTypeDataBM.eventTypeFilterList);
        let eventTypeDataVersion: EventTypeDataVersion | null = null;
        if (eventTypeDataBM.version) {
            eventTypeDataVersion = eventTypeDataVersionFactory.newEventTypeDataVersion(eventTypeDataBM.version);
        }
        updateCrossPortCallCondition(evLevelArray, crossPcCondArray);
        updateImosPortFunctionMap(evLevelArray, imosPortFuncMapArray);
        updateEventTypeInputDelayEventTypeMap(evLevelArray, eventTypeInputDelayEventTypeMapArray);
        updateEventTypeInputExcludeTimeEventTypeMap(evLevelArray, eventTypeInputExcludeTimeEventTypeMapArray);
        updateEventTypeGroupConfigChange(evLevelArray, eventTypeGroupConfigChangeArray);
        return new EventTypeData(
            evLevelArray,
            condMandatoryArray,
            eventTypeDataVersion,
            eventTypeFilterList,
            newAlternativeEventtypeGroupMapArray(eventTypeDataBM.alternateEventTypeGroupMapList),
            selectedVessel?.hasOrcaWebSimpleMode
        );
    };

    const updateCrossPortCallCondition = (evTypeLevel: EventTypeLevel[], crossPcCondArray: CrossPortCallsCondition[]) => {
        const evTypeGrArray = evTypeLevel.flatMap((o) => o.eventTypeGroupArray);
        evTypeGrArray.forEach((evTypeGr) => {
            evTypeGr.crossPortCallPreviousEvTypeGroupList = crossPcCondArray
                .filter((cr) => cr.nextEventTypeGroupId === evTypeGr.eventTypeGroupId)
                .map((o) => o.previousEventTypeGroupId);
            evTypeGr.crossPortCallNextEvTypeGroupList = crossPcCondArray
                .filter((cr) => cr.previousEventTypeGroupId === evTypeGr.eventTypeGroupId)
                .map((o) => o.nextEventTypeGroupId);
            // if (evTypeGr.levelId) {
            //     evTypeGr.crossNodeCondtion = crossNodeConditionFactory.createCrossNodeConditionItem(evTypeGr.levelId, prevList, nextList);
            // }
        });
    };

    const updateImosPortFunctionMap = (evTypeLevel: EventTypeLevel[], imosPortFuncMapArray: ImosPortFunctionMap[]) => {
        const evTypeGrArray = evTypeLevel.flatMap((o) => o.eventTypeGroupArray);
        evTypeGrArray.forEach((evTypeGr) => {
            evTypeGr.imosPortFunctionMapList = imosPortFuncMapArray
                .filter((ipfm) => ipfm.eventTypeGroupId === evTypeGr.eventTypeGroupId)
                .map((o) => o.imosPortFunction);
        });
    };

    const updateEventTypeInputDelayEventTypeMap = (
        evTypeLevel: EventTypeLevel[],
        eventTypeInputDelayEventTypeMapArray: EventTypeInputDelayEventTypeMap[]
    ) => {
        const evTypeGrArray = evTypeLevel.flatMap((o) => o.eventTypeGroupArray);
        const evTypeArray = evTypeGrArray.flatMap((o) => o.eventTypeArray);
        evTypeArray.forEach((evType) => {
            evType.eventTypeInputDelayEventTypeMapArray = eventTypeInputDelayEventTypeMapArray
                .filter((etidetm) => etidetm.eventTypeId === evType.eventTypeId)
                .map((o) => o);
        });
    };

    const updateEventTypeInputExcludeTimeEventTypeMap = (
        evTypeLevel: EventTypeLevel[],
        eventTypeInputExcludeTimeEventTypeMapArray: EventTypeInputExcludeTimeEventTypeMap[]
    ) => {
        const evTypeGrArray = evTypeLevel.flatMap((o) => o.eventTypeGroupArray);
        const evTypeArray = evTypeGrArray.flatMap((o) => o.eventTypeArray);
        evTypeArray.forEach((evType) => {
            evType.eventTypeInputExcludeTimeEventTypeMapArray = eventTypeInputExcludeTimeEventTypeMapArray
                .filter((etidetm) => etidetm.eventTypeId === evType.eventTypeId)
                .map((o) => o);
        });
    };

    const updateEventTypeGroupConfigChange = (evTypeLevel: EventTypeLevel[], eventTypeGroupConfigChangeArray: EventTypeGroupConfigChange[]) => {
        //eventTypeGroupConfigChangeArray.push(addOneTestItem());
        const evTypeGrArray = evTypeLevel.flatMap((o) => o.eventTypeGroupArray);
        evTypeGrArray.forEach((evTypeGr) => {
            evTypeGr.eventTypeGroupConfigChangeList = eventTypeGroupConfigChangeArray
                .filter((etgcc) => etgcc.eventTypeGroupId === evTypeGr.eventTypeGroupId)
                .map((o) => o);
        });
    };

    return {
        newEventTypeData,
        newEventTypeDataContext,
    };
};
